import { Observable } from "rxjs";
import {
    catchError, map
} from "rxjs/operators";

import {
    Injectable, inject
} from "@angular/core";
import {
    HttpClient, HttpHeaders
} from "@angular/common/http";

import { CaseTransformer } from "@shopliftr/common-js/data";
import {
    Task, TaskCounts, TaskableType, Note,
    TaskState
} from "@shopliftr/common-js/admin";
import {
    IFilter, Results
} from "@shopliftr/common-js/shared";
import {
    throwError
} from "rxjs";
import { IInternalApiConfig } from "../../../interfaces/internal-api-config";
import { INTERNAL_API_APP_CONFIG } from "../../../constants/iapi-app-tokens";
import { COMMON_ERROR_HANDLER } from "../../../../exceptions/constants/exception-constants";
import { InvalidParametersException } from "../../../../exceptions/invalid-parameters-exception";
import { IListFilterResponse } from "../../../../list/interfaces/list-filter-response";
import { ITaskListFilterOptions } from "./interfaces/task-list-filter-options.interface";
import { TaskListScope } from "./enums/task-list-scope";

export interface ITaskUpdateOptions {
    assignee?: string;
    override?: boolean;
}

@Injectable({ providedIn: "root" })
export class TaskRequestService {

    /**
     * Page size used when fetching every task in a flyer task tree.
     */
    static readonly RELATED_TASKS_PAGE_SIZE = 500;

    private readonly _httpClient = inject(HttpClient);
    private readonly _appConfig = inject<IInternalApiConfig>(INTERNAL_API_APP_CONFIG);
    private readonly _errorHandler = inject(COMMON_ERROR_HANDLER);

    private readonly _teamTaskUrl: string;

    private readonly _teamTaskCountsUrl: string;

    private readonly _taskUrl: string;

    private readonly _searchTaskUrl: string;

    constructor() {
        this._teamTaskUrl = `${this._appConfig.apiUrl}/v1/team/tasks`;
        this._taskUrl = `${this._appConfig.apiUrl}/v1/task`;
        this._teamTaskCountsUrl = `${this._teamTaskUrl}/counts`;
        this._searchTaskUrl = `${this._taskUrl}/search`;
    }

    getTask$(id: any): Observable<Task> {

        const url = `${this._taskUrl}/${id}`;
        return this._httpClient.get(url).pipe(
            map((response: unknown): Task => {

                return Task.deserialize(response);
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    getTaskCountsForTeam$(startDate: number, endDate: number, teamId?: string): Observable<TaskCounts> {

        const body: {
            startTime?: number;
            endTime?: number;
            teamId?: string;
        } = {};

        if (startDate) {
            body.startTime = startDate;
        }

        if (endDate) {
            body.endTime = endDate;
        }

        if (teamId) {
            body.teamId = teamId;
        }
        return this._httpClient.post(this._teamTaskCountsUrl, body).pipe(
            map((response: unknown): TaskCounts => {

                return TaskCounts.deserialize(response);
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    updateTaskState$(task: Task, transitionTargetState: TaskState, options?: ITaskUpdateOptions): Observable<Task> {

        const url = `${this._taskUrl}/${task.id}/transition`;
        const body: any = {
            transition: transitionTargetState,
            esVersion: task.esVersion
        };

        if (!task.canTransition(transitionTargetState)) {
            return throwError(() => new InvalidParametersException(`Task cannot be transitioned to : ${transitionTargetState}`));
        }

        if (options) {
            if (options.assignee) {
                body.assignee = options.assignee;
            }

            if (transitionTargetState === TaskState.Approved) {
                if (options.override) {
                    body.override = true;
                }
                else {
                    body.override = false;
                }
            }
        }

        if (transitionTargetState === TaskState.Rejected || transitionTargetState === TaskState.Waiting) {
            body.notes = Note.serializeArray(task.notes);
        }

        return this._httpClient.put(url, body).pipe(
            map((response: unknown): Task => {

                return Task.deserialize(response);
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    updateTaskNotes$(task: Task, notes: Array<Note>): Observable<Task> {

        const url = `${this._taskUrl}/${task.id}`;
        const body: any = {
            notes: Note.serializeArray(notes),
            esVersion: task.esVersion
        };

        return this._httpClient.put(url, body).pipe(
            map((response: unknown): Task => {

                return Task.deserialize(response);
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    assignTask$(task: Task, userId: string, remember: boolean): Observable<Task> {

        const url = `${this._taskUrl}/${task.id}/assign`;
        const body = {
            assignee: userId || null,
            remember: remember,
            esVersion: task.esVersion
        };

        return this._httpClient.put(url, body).pipe(
            map((response: unknown): Task => {

                return Task.deserialize(response);
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    unassignTask$(task: Task): Observable<Task> {

        const url = `${this._taskUrl}/${task.id}/unassign`;

        const body = {
            esVersion: task.esVersion
        };

        return this._httpClient.put(url, body).pipe(
            map((response: unknown): Task => {

                return Task.deserialize(response);
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    update$(task: Task): Observable<Task> {

        const headers = new HttpHeaders({ "Content-Type": "application/json" });
        const url = `${this._taskUrl}/${task.id}`;

        const values: any = {};
        for (const field in task.serialize()) {
            if (task[field] !== null) {
                values[field] = task[field];
            }
        }

        return this._httpClient.put(url, JSON.stringify(values), { headers: headers }).pipe(
            map((response: unknown): Task => {

                return Task.deserialize(response);
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    filterTasks$(filters: Array<IFilter>, size: number, lastIndex: number, teamId?: string): Observable<Results<Task>> {

        const body: any = {
            filters: filters,
            size: size
        };

        if (lastIndex) {
            body["startIndex"] = lastIndex;
        }

        if (teamId) {
            body["teamId"] = teamId;
        }

        return this._httpClient.post(this._searchTaskUrl, body).pipe(
            map((response: unknown): Results<Task> => {

                return Results.deserialize(response, CaseTransformer.camelCase, Task);
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    /**
     * Fetches every other task in the flyer task tree that the given task belongs to.
     *
     * Searches by the task's `rootFlyerId` using a single page of
     * {@link TaskRequestService.RELATED_TASKS_PAGE_SIZE} results, then drops the task itself.
     *
     * @param task - The task whose tree is wanted
     * @returns The related tasks, excluding the task itself
     */
    getRelatedTasks$(task: Task): Observable<Array<Task>> {

        return this.filterTasks$([{
            type: "rootFlyerId",
            name: "",
            value: task.rootFlyerId
        }], TaskRequestService.RELATED_TASKS_PAGE_SIZE, 0).pipe(
            map((results: Results<Task>): Array<Task> => {

                return results.results.filter((related) => related.id !== task.id);
            })
        );
    }

    /**
     * Searches tasks for a list.
     *
     * Owns the translation from {@link ITaskListFilterOptions} to the v1 `/task/search`
     * filter vocabulary. The recognised filter types are fixed by TaskDaoElastic —
     * anything else is rejected server-side with a BadRequestException.
     *
     * Sort is fixed server-side (dueDate, priority, description.sort); there is no sort input.
     *
     * @param options - The filter options describing the scope and optional constraints
     * @param pageIndex - Zero-based page index
     * @param pageSize - Number of results per page
     * @returns Observable of the filtered task response with results and total count
     */
    searchTasks$(
        options: ITaskListFilterOptions,
        pageIndex: number,
        pageSize: number
    ): Observable<IListFilterResponse<Task>> {

        if (!options) {
            return throwError(() => new InvalidParametersException("options is required"));
        }

        const filters: Array<IFilter> = this._buildSearchFilters(options);
        const startIndex = pageIndex * pageSize;

        const body: Record<string, unknown> = {
            filters: filters,
            size: pageSize
        };

        if (startIndex) {
            body.startIndex = startIndex;
        }

        if (options.teamId) {
            body.teamId = options.teamId;
        }

        return this._httpClient.post(this._searchTaskUrl, body).pipe(
            map((response: unknown): IListFilterResponse<Task> => {

                const results = Results.deserialize(response, CaseTransformer.camelCase, Task);

                return {
                    results: results.results,
                    total: results.total
                };
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    /**
     * @param startDate
     * @param endDate
     * @param description
     * @param priority
     * @param teamId
     * @param assignableType
     * @param assignableId
     * @param taskableType
     * @param taskableId Can be FlyerVersion or String
     * @param notes
     */
    createFlyerTask$(
        startDate: Date,
        endDate: Date,
        description: string,
        priority: number,
        teamId: string,
        assignableType: string,
        assignableId: string,
        notes: Array<Note>,
        flyerVersionId: string
    ): Observable<Task> {

        const body = {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            description: description,
            priority: priority,
            teamId: teamId,
            assignableType: assignableType,
            assignableId: assignableId,
            taskableType: TaskableType.Flyer,
            notes: Note.serializeArray(notes),
            flyerVersionId
        };

        return this._httpClient.post(`${this._taskUrl}`, body).pipe(
            map((response: unknown): Task => {

                return Task.deserialize(response);
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    /**
     * Maps a scope and the free-form filters onto the v1 filter vocabulary.
     *
     * @param options - The filter options to translate
     * @returns Array of IFilter objects for the v1 search endpoint
     */
    private _buildSearchFilters(options: ITaskListFilterOptions): Array<IFilter> {

        const filters = new Array<IFilter>();

        switch (options.scope) {
            case TaskListScope.Team:
                filters.push({
                    name: "Team",
                    type: "team"
                });
                break;
            case TaskListScope.TeamActive:
                filters.push({
                    name: "Active",
                    type: "active"
                });
                break;
            case TaskListScope.TeamLate:
                filters.push({
                    name: "Late",
                    type: "late"
                });
                break;
            case TaskListScope.Unassigned:
                filters.push({
                    name: "Unassigned",
                    type: "unassigned"
                });
                break;
            case TaskListScope.Mine:
                filters.push(
                    {
                        name: "User",
                        type: "user"
                    },
                    {
                        name: "Available",
                        type: "available"
                    }
                );
                break;
            case TaskListScope.MyLate:
                filters.push(
                    {
                        name: "User",
                        type: "user"
                    },
                    {
                        name: "Late",
                        type: "late"
                    }
                );
                break;
            case TaskListScope.PendingApproval:
                filters.push({
                    name: "Pending",
                    type: "pending"
                });
                break;
            case TaskListScope.Approved:
                filters.push({
                    name: "Approved",
                    type: "approved"
                });
                break;
        }

        if (options.restrictToCurrentUser && options.scope !== TaskListScope.Mine
            && options.scope !== TaskListScope.MyLate) {
            filters.push({
                name: "User",
                type: "user"
            });
        }

        if (options.startTime) {
            filters.push({
                name: "Start Time",
                type: "startTime",
                value: options.startTime.valueOf()
            });
        }

        // The `late` scopes make the server set its own endTime; sending one too is misleading.
        const isLateScope = options.scope === TaskListScope.TeamLate || options.scope === TaskListScope.MyLate;

        if (options.endTime && !isLateScope) {
            filters.push({
                name: "End Time",
                type: "endTime",
                value: options.endTime.valueOf()
            });
        }

        const description = options.description?.trim();

        if (description) {
            filters.push({
                name: "Description",
                type: "description",
                value: description
            });
        }

        return filters;
    }
}
